import { Outlet, Link } from 'react-router-dom';
import { Suspense, lazy, useMemo } from 'react';
import { Loader2, Rocket, HelpCircle } from 'lucide-react';
import { DashboardProvider, useDashboard } from '@/src/views/dashboard/context';
import { ChatBubble } from '@/src/components/chat';
import DashboardSearch from '@/src/components/ui/spotlight';
import { useSidebar } from './ProtectedLayout';
import type { FilterState } from '@/src/views/dashboard/types';

// Loading fallback
const TabLoader = () => (
  <div className="flex items-center justify-center p-8 min-h-[200px]">
    <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
  </div>
);

// Dynamic imports for navigation and filters
const DashboardNavigation = lazy(() =>
  import('@/src/views/dashboard/components/dashboard-navigation').then((m) => ({
    default: m.DashboardNavigation,
  }))
);

const DashboardFilters = lazy(() =>
  import('@/src/components/dashboard-filters').then((m) => ({ default: m.DashboardFilters }))
);

const DashboardControls = lazy(() =>
  import('@/src/views/dashboard/components/dashboard-controls').then((m) => ({
    default: m.DashboardControls,
  }))
);

const FilterPanelContent = lazy(() =>
  import('@/src/components/ui/filters-panel-content').then((m) => ({ default: m.FilterPanelContent }))
);

/**
 * Dashboard Layout for Vite
 *
 * Mirrors the Next.js dashboard layout structure:
 * - Wraps all dashboard routes with DashboardProvider for shared state
 * - Provides navigation, filters, and controls header
 * - Uses Outlet for nested route rendering
 */
export function DashboardLayout() {
  const { showSidebar, setShowSidebar } = useSidebar();

  return (
    <DashboardProvider>
      <DashboardLayoutContent showSidebar={showSidebar} setShowSidebar={setShowSidebar} />
    </DashboardProvider>
  );
}

interface DashboardLayoutContentProps {
  showSidebar: boolean;
  setShowSidebar: (show: boolean) => void;
}

function DashboardLayoutContent({ showSidebar, setShowSidebar }: DashboardLayoutContentProps) {
  // Get all dashboard context values
  const {
    activeTab,
    realTimeConfig,
    setRealTimeConfig,
    filters,
    setFilters,
    handleFilterChange,
    handleReload,
    isDateRangeToday,
    isFilterPanelExpanded,
    setIsFilterPanelExpanded,
    filterOptions,
    activeFiltersCount,
    showPerformanceChart,
    showMetricCards,
    showTrafficSource,
    showCategories,
    showGeoMap,
    showDevices,
    showOffers,
    showLandingPages,
    showSuspicious,
    showMediaBuyers,
    showCampaignList,
    showProfitabilityCards,
    showRecommendations,
    isCompactMetrics,
    setShowPerformanceChart,
    setShowMetricCards,
    setShowTrafficSource,
    setShowCategories,
    setShowGeoMap,
    setShowDevices,
    setShowOffers,
    setShowLandingPages,
    setShowSuspicious,
    setShowMediaBuyers,
    setShowCampaignList,
    setShowProfitabilityCards,
    setShowRecommendations,
    setIsCompactMetrics,
    resetVisibility,
    controlOrder,
    setControlOrder,
  } = useDashboard();

  // Determine if filter bar should be shown (overview and campaigns tabs)
  const shouldShowFilters = activeTab === 'overview' || activeTab === 'campaigns' || activeTab === 'reports';

  // Memoize the campaigns with active filter applied (for default selection)
  const campaignsWithActiveFilter = useMemo(() => {
    if (filters.campaigns.length === 0) {
      return (filterOptions.campaigns || []).filter((c: any) => c?.status === 'active') as FilterState['campaigns'];
    }
    return filters.campaigns;
  }, [filters.campaigns, filterOptions.campaigns]);

  return (
    <div className="p-6 relative flex flex-col w-full">
      {/* Dashboard Header */}
      <div data-testid="dashboard-header" className="flex items-center justify-between mb-6">
        <div data-testid="dashboard-title-container" className="flex items-center gap-2">
          <Rocket className="w-8 h-8" />
          <h1 data-testid="dashboard-title" className="text-2xl font-bold">
            Clickster Command Center
          </h1>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to="/knowledge"
            className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors"
            data-testid="dashboard-knowledge-link"
            title="Knowledge Base"
          >
            <HelpCircle className="w-5 h-5 stroke-[1.5]" />
          </Link>
          <DashboardSearch />
        </div>
      </div>

      {/* Navigation with filters */}
      <Suspense fallback={<TabLoader />}>
        <DashboardNavigation
          activeTab={activeTab}
          showSidebar={showSidebar}
          setShowSidebar={setShowSidebar}
          className={activeTab === 'overview' ? '' : 'py-4'}
          realTimeConfig={realTimeConfig}
          setRealTimeConfig={setRealTimeConfig}
          isDateRangeToday={isDateRangeToday}
          isFilterPanelExpanded={shouldShowFilters ? isFilterPanelExpanded : false}
          filterPanelPosition="relative"
          filterPanelClassName="w-full border-t border-gray-200 dark:border-gray-700 bg-background"
          filterPanelContent={shouldShowFilters ? (
            <Suspense fallback={<TabLoader />}>
              <FilterPanelContent
                mediaBuyerOptions={filterOptions.mediaBuyers || []}
                trafficSourceOptions={filterOptions.trafficSources || []}
                campaignOptions={filterOptions.campaigns || []}
                categoryOptions={filterOptions.categories || []}
                rotationGroupsOptions={filterOptions.rotationGroups || []}
                offerOptions={filterOptions.offers || []}
                affiliateOptions={filterOptions.affiliates || []}
                selectedFilters={filters}
                disabledOptionsEffect={false}
                autoSetFilters
                customComponentInFiltersSection={{
                  show: true,
                  label: 'Advanced Filters',
                  config: {
                    showIncludedOffer: true,
                    showBootTraffic: true,
                    showOrphanedTraffic: true,
                    showDateBasedOn: false,
                    showConversionMode: false,
                    showOfferClick: true,
                    showIncomeTypes: true,
                    showTrafficType: false,
                    showDatasource: true,
                  }
                }}
                canCategorySelectOnlyOne={false}
                showAffiliatesAndOffers={true}
                onFilterChange={() => { }}
                onReset={() => {
                  const resetFilters = {
                    ...filters,
                    mediaBuyers: [],
                    trafficSources: [],
                    categories: [],
                    rotationGroups: [],
                    offers: [],
                    affiliates: [],
                    compareDates: {
                      ...filters.compareDates,
                      dates: [undefined, undefined] as [Date | undefined, Date | undefined],
                      data: [],
                      isLoading: false,
                      isApplied: false
                    }
                  };
                  setFilters(resetFilters);
                }}
                onApply={(appliedFilters) => {
                  handleFilterChange(appliedFilters);
                  setIsFilterPanelExpanded(false);
                }}
                hideDatePicker
                hideGroupBy={true}
                showFooter={true}
              />
            </Suspense>
          ) : undefined}
          filters={filters}
          setFilters={setFilters}
          onFilterChange={handleFilterChange}
          onReload={handleReload}
          showPerformanceChart={showPerformanceChart}
          showMetricCards={showMetricCards}
          showTrafficSource={showTrafficSource}
          showCategories={showCategories}
          showGeoMap={showGeoMap}
          showDevices={showDevices}
          showOffers={showOffers}
          showLandingPages={showLandingPages}
          showSuspicious={showSuspicious}
          showMediaBuyers={showMediaBuyers}
          showCampaignList={showCampaignList}
          showProfitabilityCards={showProfitabilityCards}
          setShowPerformanceChart={setShowPerformanceChart}
          setShowMetricCards={setShowMetricCards}
          setShowTrafficSource={setShowTrafficSource}
          setShowCategories={setShowCategories}
          setShowGeoMap={setShowGeoMap}
          setShowDevices={setShowDevices}
          setShowOffers={setShowOffers}
          setShowLandingPages={setShowLandingPages}
          setShowSuspicious={setShowSuspicious}
          setShowMediaBuyers={setShowMediaBuyers}
          setShowCampaignList={setShowCampaignList}
          setShowProfitabilityCards={setShowProfitabilityCards}
          controlOrder={controlOrder}
          setControlOrder={setControlOrder}
        >
          {/* DashboardFilters rendered as children - appears in navigation row */}
          {shouldShowFilters && (
            <div>
              <Suspense fallback={<TabLoader />}>
                <DashboardFilters
                  showCompareDates={true}
                  trafficSourceOptions={filterOptions.trafficSources || []}
                  campaignOptions={filterOptions.campaigns || []}
                  categoryOptions={filterOptions.categories || []}
                  selectedFilters={filters}
                  onFilterChange={handleFilterChange}
                  onApply={handleFilterChange}
                  onReload={handleReload}
                  showDatePicker={true}
                  showFilterBadge={true}
                  showDatePickerInPanel={false}
                  filterPanelClassName="hidden"
                  isExpanded={isFilterPanelExpanded}
                  onExpandedChange={setIsFilterPanelExpanded}
                  showRealTimePreset={true}
                  realTimeConfig={realTimeConfig}
                  onRealTimeConfigChange={setRealTimeConfig}
                  customReloadComponent={
                    // Only show DashboardControls for overview and campaigns tabs
                    (activeTab === 'overview' || activeTab === 'campaigns') ? (
                      <Suspense fallback={null}>
                        <DashboardControls
                          view={activeTab}
                          controlOrder={controlOrder}
                          setControlOrder={setControlOrder}
                          showMetricCards={showMetricCards}
                          setShowMetricCards={setShowMetricCards}
                          isCompactMetrics={isCompactMetrics}
                          setIsCompactMetrics={setIsCompactMetrics}
                          showPerformanceChart={showPerformanceChart}
                          setShowPerformanceChart={setShowPerformanceChart}
                          showTrafficSource={showTrafficSource}
                          setShowTrafficSource={setShowTrafficSource}
                          showCategories={showCategories}
                          setShowCategories={setShowCategories}
                          showGeoMap={showGeoMap}
                          setShowGeoMap={setShowGeoMap}
                          showDevices={showDevices}
                          setShowDevices={setShowDevices}
                          showOffers={showOffers}
                          setShowOffers={setShowOffers}
                          showLandingPages={showLandingPages}
                          setShowLandingPages={setShowLandingPages}
                          showSuspicious={showSuspicious}
                          setShowSuspicious={setShowSuspicious}
                          showMediaBuyers={showMediaBuyers}
                          setShowMediaBuyers={setShowMediaBuyers}
                          showCampaignList={showCampaignList}
                          setShowCampaignList={setShowCampaignList}
                          showProfitabilityCards={showProfitabilityCards}
                          setShowProfitabilityCards={setShowProfitabilityCards}
                          showRecommendations={showRecommendations}
                          setShowRecommendations={setShowRecommendations}
                          onReset={resetVisibility}
                          activeFiltersCount={activeFiltersCount}
                        />
                      </Suspense>
                    ) : undefined
                  }
                />
              </Suspense>
            </div>
          )}
        </DashboardNavigation>
      </Suspense>

      {/* Main content area */}
      <div data-testid="dashboard-content-section" className="flex-1 py-6 w-full">
        <Suspense fallback={<TabLoader />}>
          <Outlet />
        </Suspense>
      </div>

      {/* AI Chat Bubble */}
      <ChatBubble
        title="Clickster AI Assistant"
        placeholder="Type your message..."
      />
    </div>
  );
}
